import Lottie from 'lottie-react';
import React, { createContext, useContext, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import SuccessAnimation from "assets/images/SuccessAnimation.json";
import ErrorAnimation from "assets/images/ErrorAnimation.json";
import WarningAnimation from "assets/images/WarningAnimation.json";

const ModalContext = createContext();
let externalShowModal = null;

const LoadingSpinner = () => (
  <div className="h-28 flex items-center justify-center">
    <div className="h-20 w-20 animate-spin rounded-full border-[10px] border-dashed border-[#22C55E] border-r-green-100" />
  </div>
);

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  const [modal, setModal] = useState({
    visible: false,
    content: null,
    info: "success",
    showConfirm: false,
    onConfirm: null,
    onCancel: null,
    confimLabel: "",
    cancelLabel: "",
  });

  const timeoutRef = useRef(null);

  const showModal = ({ content, info = "success", showConfirm = false, onConfirm = null, onCancel = null, confimLabel = "", cancelLabel = "" }) => {
    setModal({ visible: true, content, info, showConfirm, onConfirm, onCancel, confimLabel, cancelLabel });

    // Auto-close only if confirmation buttons are NOT shown
    if (!showConfirm) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        closeModal();
      }, 3000);
    }
  };

  const closeModal = () => {
    clearTimeout(timeoutRef.current);
    setModal({
      visible: false,
      content: null,
      info: "success",
      showConfirm: false,
      onConfirm: null,
      onCancel: null,
      confimLabel: "",
      cancelLabel: "",
    });
  };

  externalShowModal = showModal;

  const handleConfirm = () => {
    modal.onConfirm?.();
    closeModal();
  };

  const handleCancel = () => {
    if(typeof(modal.onCancel) !== "boolean") {
      modal.onCancel?.();
    }
    closeModal();
  };

  const renderAnimation = (info) => {
    switch (info) {
      case "success":
        return <Lottie animationData={SuccessAnimation} loop={false} className='h-28' />
      case "error":
        return <Lottie animationData={ErrorAnimation} loop={false} className='h-28' />
      case "warning":
        return <Lottie animationData={WarningAnimation} loop={false} className='h-28' />
      case "loading":
        return <LoadingSpinner />
      default:
        return (!!info ? <Lottie animationData={WarningAnimation} loop={false} className='h-28' /> : <></>)
    }
  }

  return (
    <ModalContext.Provider value={{ showModal }}>
      {children}
      {modal.visible &&
        ReactDOM.createPortal(
          <div style={modalStyles.overlay}>
            <div className='max-w-md w-full m-auto bg-[white] rounded-3xl p-6 shadow-lg outline-none'>
              <div className='flex flex-col items-center px-6 gap-5'>
                {renderAnimation(modal.info)}
                <div className='text-center font-manropeM text-[2.6vh]'>{modal.content}</div>
                {modal.showConfirm && (
                  <div className='flex gap-4 mt-4'>
                    {!!modal.onConfirm && <button onClick={handleConfirm} className='inline-flex items-center px-[3vh] py-[1vh] text-[2vh] font-medium text-center text-[white] bg-blue-700 rounded-md hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none'>
                      {modal?.confimLabel || "Yes"}
                    </button>}
                    {!!modal.onCancel && <button type="button" onClick={handleCancel} className="px-[3vh] py-[1vh] text-[2vh] font-medium text-gray-900 bg-[white] border border-gray-300 rounded-md focus:outline-none hover:bg-gray-100 hover:text-blue-700 focus:z-[2000] focus:ring-4 focus:ring-gray-100">
                      {modal?.cancelLabel || "No"}
                    </button>}
                  </div>)}
              </div>
            </div>
          </div>,
          document.getElementById('root')
        )}
    </ModalContext.Provider>
  );
};

export const showGlobalModal = (content, info = "success", options = {}) => {
  if (externalShowModal) {
    externalShowModal({ content, info, ...options });
  } else {
    console.warn('Modal system not ready');
  }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10003,
  },
};
